import { CommonModule } from '@angular/common';
import { Component, computed, ElementRef, inject, input, OnInit, signal, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { GoBackComponent } from 'shared/components/go-back/go-back.component';
import { IdDocumentComponent } from '../tutorial-okeey-cash/id-document/id-document.component';
import { UsAddressComponent } from '../tutorial-okeey-cash/us-address/us-address.component';
import { OkeeyCashSlideService } from '../core/services/okeey-cash-slide.service';
import { SlideContentComponent } from '../core/component/slide-content/slide-content.component';
import { SlideControlComponent } from '../core/component/slide-control/slide-control.component';
import { Slide, SlideTypes } from '../core/Type/okeeycash-slide.interface';

@Component({
  selector: 'okeey-cash-slider',
  imports: [
    CommonModule,
    GoBackComponent,
    IdDocumentComponent,
    UsAddressComponent,
    SlideContentComponent,
    SlideControlComponent
  ],
  templateUrl: './okeey-cash-slider.component.html',
  styleUrl: './okeey-cash-slider.component.scss'
})
export class OkeeyCashSliderComponent implements OnInit {
  @ViewChild('slidesContainer') private slidesContainer?: ElementRef<HTMLElement>;
  @ViewChild(IdDocumentComponent) private idDocumentComponent?: IdDocumentComponent;

  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly slideService = inject(OkeeyCashSlideService);

  readonly slides = input<Slide[]>([]);

  readonly activeSlideIndex = signal(0);
  readonly routerUrl = this.router.url;
  readonly isTutorial = this.routerUrl.includes('tutorial');
  readonly JourneyRoute = this.routerUrl.includes('journey');
  readonly SlideType = SlideTypes;
  readonly preparedSlides = computed(() =>
    this.isTutorial
      ? this.slideService.manipulateSlides(
          [...this.slides()],
          [
            { index: 5, type: SlideTypes.ID_DOCUMENT },
            { index: 7, type: SlideTypes.US_ADDRESS }
          ]
        )
      : this.slides()
  );
  readonly isPrevDisabled = computed(() => this.activeSlideIndex() === 0);
  readonly isLastTutorialSlide = computed(
    () => this.isTutorial && this.activeSlideIndex() === this.preparedSlides().length - 1
  );
  readonly isNextDisabled = computed(
    () => this.activeSlideIndex() === this.preparedSlides().length - 1 && !this.isLastTutorialSlide()
  );
  readonly slidesTransform = computed(() => 'translateX(' + -this.activeSlideIndex() * 100 + '%)');

  ngOnInit(): void {
    if (this.isTutorial) {
      const selectedIndex = this.slideService.selectedIndex;
      this.goToSlide(selectedIndex >= 5 ? selectedIndex + 1 : selectedIndex);
    }
  }

  goToSlide(index: number): void {
    if (!Number.isInteger(index) || index < 0 || index >= this.preparedSlides().length) {
      return;
    }

    this.idDocumentComponent?.closePanels();
    this.activeSlideIndex.set(index);

    const activeSlide = this.slidesContainer?.nativeElement.children[index];
    if (activeSlide instanceof HTMLElement) {
      activeSlide.scrollTop = 0;
    }
  }

  onPrevClick(): void {
    if (!this.isPrevDisabled()) {
      this.goToSlide(this.activeSlideIndex() - 1);
    }
  }

  onNextClick(): void {
    if (this.isLastTutorialSlide()) {
      void this.router.navigate(['ready-to-apply'], { relativeTo: this.route.parent });
    } else if (!this.isNextDisabled()) {
      this.goToSlide(this.activeSlideIndex() + 1);
    }
  }
}
